import React from 'react'
import { Link } from 'react-router-dom'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material'
import styled from '@emotion/styled'
import LaunchIcon from '@mui/icons-material/Launch'
import { formatDateTime } from '@npm-client-frontends/shared-utils/core'
import { DatePreference } from '@npm-client-frontends/client-frontends-shared-types'
import { CardContainer } from './CardContainer'

type DetailsTableProps<T> = {
  title: string
  entity: T
  fields: DetailsTableFields
  userDateFormat?: DatePreference
}

export type DetailsTableFields = {
  [fieldName: string]: DetailsTableField
}

export type DetailsTableField = {
  type:
    | 'link'
    | 'entity'
    | 'orgList'
    | 'custom'
    | 'field'
    | 'region'
    | 'userFormattedDate'
  label: string
  visible?: boolean
  renderElement?: () => JSX.Element
}

export const ItemLink = styled(Link)({
  cursor: 'pointer',
  color: '#313C64',
  fontWeight: 'bold',
  textDecoration: 'none',

  '&:hover': {
    textDecoration: 'underline',
  },
})

export const DetailsTable = <T,>({
  fields,
  entity,
  title,
  userDateFormat,
}: DetailsTableProps<T>) => {
  const renderField = (
    fieldName: string,
    field: DetailsTableField,
  ): JSX.Element | undefined => {
    switch (field.type) {
      case 'link':
        return (
          <ItemLink
            to={entity[fieldName].url}
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            URL
            <LaunchIcon fontSize="small" sx={{ marginLeft: '3px' }} />
          </ItemLink>
        )

      case 'entity':
        return (
          <>
            {entity[fieldName].map((entity) => (
              <label key={entity.id}>{entity.identifier}</label>
            ))}
          </>
        )

      case 'orgList':
        return (
          <>
            {entity[fieldName].map((orgEntity) => (
              <ItemLink key={orgEntity.id} to={`/view/org/${orgEntity.id}`}>
                {orgEntity.identifier}
              </ItemLink>
            ))}
          </>
        )

      case 'field':
        return <>{entity[fieldName]}</>

      case 'region':
        return (
          <>
            {entity[fieldName].map((region) => (
              <ItemLink key={region.id} to={`/view/region/${region.id}`}>
                {region.identifier}
              </ItemLink>
            ))}
          </>
        )

      case 'userFormattedDate':
        if (!userDateFormat) throw new Error('userDateFormat is required')
        return <>{formatDateTime(entity[fieldName], userDateFormat)}</>

      default:
        return field.renderElement?.()
    }
  }

  const isFieldVisible = (
    fieldName: string,
    field: DetailsTableField,
  ): boolean => {
    switch (field.type) {
      case 'link':
        return !!entity[fieldName]?.url
      case 'entity':
      case 'orgList':
      case 'region':
        return entity[fieldName]?.length > 0
      case 'field':
      case 'userFormattedDate':
        return entity[fieldName] !== null && entity[fieldName] !== undefined
      default:
        return !!field.visible
    }
  }

  return (
    <CardContainer title={title}>
      <TableContainer>
        <Table>
          <TableBody
            sx={{
              'tr:last-child td': {
                borderBottom: 0,
              },
              '& tr:nth-of-type(even)': {
                backgroundColor: '#f5f5f5',
              },
              '& tr:nth-of-type(odd)': {
                backgroundColor: '#ffffff',
              },
              td: {
                padding: '10px 16px',
                fontSize: '13px',
              },
            }}
          >
            {Object.entries(fields).map(([fieldName, field], index) => {
              if (!isFieldVisible(fieldName, field)) return null

              return (
                <TableRow key={index}>
                  <TableCell sx={{ width: '35%', fontWeight: '600' }}>
                    {field.label}
                  </TableCell>
                  <TableCell
                    sx={{
                      '> :not(:last-child):after': {
                        content: '" | "',
                        whiteSpace: 'pre',
                        fontWeight: 'normal',
                      },
                    }}
                  >
                    {renderField(fieldName, field)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </CardContainer>
  )
}
